
import Request from './request';
import Util from './util';

import * as log from 'loglevel';

/**
 * Shopify Store helper
 *
 * can be used as singleton via ShopifyCart.getInstance(..);
 */
class ShopifyStore {
    constructor(options) {
        // Default settings
        let defaults = {
            api: '',
            path: {
                products: '/products/{handle}.{view}',
            }
        };

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});
    }

    static getInstance(options){
        if(ShopifyStore._instance){
            return ShopifyStore._instance;
        }

        // store instance on class as static property
        ShopifyStore._instance = new ShopifyStore(options); // options only required on first call

        return ShopifyStore._instance;
    }

    fakeProduct(handle){
        // overwrite product functions
        this.isProduct = () => true;
        this.getHandleFromUrl = () => handle;
    }

    isAdmin() {
        return document.getElementById('admin_bar_iframe') != null;
    }

    isProduct(){
        return window.location.pathname.indexOf('/products/') !== -1 || this.isProductPreview();
    }

    isProductPreview(){
        return window.location.pathname.indexOf('/products_preview') !== -1;
    }

    getDiscountUrl(code, redirect) {
        return '/discount/' + code + ( redirect ? ('?redirect=' + redirect) : '');
    }

    getDiscountCode(){
        return Util.readCookie('discount_code'); // cookie set by navigating to discount url
    }

    saveDiscountCode(code){
        return Util.writeCookie('discount_code', code, null, '/'); // like set by shopify as 'session' cookie
    }

    removeDiscountCode(){
        return Util.removeCookie('discount_code');
    }

    getCustomerId(){
        // possible to set via app-embed (currently NOT set as the below approach seems to work)
        if(window.theme && window.theme.customerId){
            return window.theme.customerId; // {%- if customer -%}window.theme.customerId = {{ customer.id }};{%- endif -%}
        }
        // some themes the meta variable filled
        if(window.meta && window.meta.page && window.meta.page.customerId){
            return window.meta.page.customerId;
        }
        // fallback shopify analytics (works on most themes)
        if(window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId){
            return window.ShopifyAnalytics.meta.page.customerId;
        }
        return null;
    }


    getHandleFromUrl(url = location.pathname) {

        // NEW: shopify markets global vars for routes > remove locale from handle (ONLY required for product previews with locale folder)
        if(window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root !== '/'){
            url = url.replace(window.Shopify.routes.root, '/');
        }

        let cleaned = url.replace(/\/$/, '').split('/');
        let handle = (
            ('products' == cleaned[cleaned.length - 2] || 'products_preview' === cleaned.pop()) &&
            cleaned[cleaned.length - 1].replace(/(.*)?\?.*$/, '$1')
        );
        // decode uri to support custom utf8 signs like japanese
        return decodeURIComponent(handle).toLowerCase();
    }

    tryGetProduct(){
        if(this.isProduct()){
            return this.getProduct();
        } else {
            // resolve promise with null product
            return new Promise((resolve, reject) => {
                resolve(null);
            });
        }
    }

    // returns all the products currently in the local store as map with handle(.view) as key
    async tryGetProductMap(){

        // NEW: Dawn theme featured products support
        if(this.isTheme('Dawn') || this.isTheme('Horizon')){
            await this.parseOfferJsonElements();
        }

        // single product support ( IMPORTANT: for themes that have no ProductJson html element! )
        if(this.isProduct()){
            // fetch product based on url if not already in cache
            await this.getProduct();
        }

        // product already in store
        return ShopifyStore.products;
    }

    // INFO: Dawn theme is missing ProductJson element on featured products - so we fallback to a custom offer json element that is present in the theme
    parseOfferJsonElements(){

        /* FIX: Dawn featured product has a standard ID product form tag for the installment form (payment_terms): ?key=sections%2Ffeatured-product.liquid&line=196
               on product page it has a correct custom id: ?key=sections%2Fmain-product.liquid&line=123
               our app always prefers the standard ID via #product_form_<product-id> which causes the featured product to attach to the wrong form
               we now manually change the id="product_form_" to contain the installment prefix to be the same as on the product detail page (ignored by our app)
        */
        document.querySelectorAll('.featured-product form[id^="product_form_"]').forEach((formEl) => {
            formEl.setAttribute('id', formEl.getAttribute('id').replace('product_form_', 'product-form-installment-featured--'));
            log.debug('updated featured product form id: ', formEl, formEl.id);
        });

        let productJsonElArray = document.querySelectorAll('script[type="application/ld+json"]');

        let promises = [];

        if(productJsonElArray.length > 0){
            for(let i=0; i<productJsonElArray.length; i++){
                let productJsonEl = productJsonElArray[i];
                try {
                    // parse json
                    let productJson = JSON.parse(productJsonEl.innerHTML);

                    // NEW: check product url & .offers JSON-LD can generated by: {{ section.settings.product | structured_data }}
                    const isProduct = typeof productJson.url === 'string' && /\/products\/[^/?#]+/.test(productJson.url) &&
                        // extra check to avoid false positives
                        ( productJson['@type'] === 'Product' || productJson['@type'] === 'ProductGroup' );

                    if(isProduct){

                        // extract handle from url
                        let handleKey = this.getHandleFromUrl(productJson.url) + '.js';

                        // not yet in cache?
                        if(!ShopifyStore.products[handleKey]){
                            promises.push(new Promise((resolve, reject) => {

                                // fetch product from as js format via api
                                let request = new Request(productJson.url + '.js', {
                                    method: 'GET',
                                    responseType: 'json'
                                });

                                return request
                                    .then((response) => {
                                        // store in cache
                                        let product = response.data;
                                        ShopifyStore.products[handleKey] = product;
                                        // return product json
                                        resolve(product);
                                    })
                                    .catch((error) => {
                                        log.error('failed to fetch product: ', productJson.url, error);
                                        // NEW: ignore error and resolve with null to avoid breaking product detail lookup
                                        resolve(null);
                                    });

                            }));
                        }
                    }


                } catch (e) {
                    log.debug('error parsing ProductOffer: ', productJsonEl, e);
                }

            }
        }

        return Promise.all(promises);
    }

    // parses all ProductJson elements found on the page
    // js view is the format that the product is stored as json in the DOM by some themes by using the json filter:  {{ product | json }}
    parseProductJsonElements(){
        // find all elements starting with 'ProductJson-'
        let productJsonElArray = document.querySelectorAll('[id^="ProductJson-"]');

        // NEW: custom themes have non standard json definitions
        if(productJsonElArray.length === 0){
            productJsonElArray = document.querySelectorAll('[data-product-json], [data-section-type="dynamic-featured-product"]');
        }

        if(productJsonElArray.length > 0){

            for(let i=0; i<productJsonElArray.length; i++){
                let productJsonEl = productJsonElArray[i];
                try {
                    // parse json
                    let productJson = JSON.parse(productJsonEl.innerHTML);

                    // NEW: some themes have the product nested
                    if(typeof productJson.product === 'object'){
                        productJson = productJson.product;
                    }

                    // check if product parsing was successful
                    if(productJson.handle && productJson.variants?.length){ // NEW: some themes just store partial product info - extra check on variants
                        // store in local cache
                        ShopifyStore.products[productJson.handle.toLowerCase() + '.js'] = this.convertProductJson(productJson);
                    }
                } catch (e) {
                    log.debug('error parsing ProductJson: ', productJsonEl, e);
                }

            }
        }
    }

    convertProductJson(productJson){
        // https://socialpix-dev.myshopify.com/products/live-product-options.js  and {{ product | json }}
        // http://jsondiff.com/  mainly .options is different  - .options could be recreated base don variants

        // convert options to match with format from url
        if(productJson.options && productJson.options.length && typeof productJson.options[0] !== 'object'){
            /* js options format
            {"id": 8466092261481,"product_id": 6592601325673,"name": "Size","position": 1,"values": ["small","medium","big"]}
             */
            let options = [];

            for (let i = 0; i < productJson.options.length; i++) {
                let option =  {
                    name: productJson.options[i],
                    position: i + 1,
                    values: []
                }

                // build values from variants
                for (let j = 0; j < productJson.variants.length; j++) {
                    const variant = productJson.variants[j];
                    let value = variant['option' + option.position];
                    // add value if not already
                    if(value && option.values.indexOf(value) === -1){
                        option.values.push(value);
                    }
                }

                options.push(option);
            }

            productJson.options = options;
        }

        // NEW: image format conversion (some themes have different image formats)
        if(productJson.images && productJson.images.length){
            productJson.images = productJson.images.map((image) => {
                // check if object
                if(typeof image === 'object'){
                    if(image.src){
                        return image.src;
                    } else if(image.url){
                        return image.url;
                    } else if(image.sizes?.original){ // probably unique to a specific theme
                        return image.sizes.original;
                    }
                    return image;
                }
                // src only
                return image;
            });

        }

        return productJson;
    }

    // default view is .js ( described in shopify ajax docs: https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#get-product )
    // js view also works if the product is already provided in the HTML via ProductJson- tag
    getProduct(handle, view = 'js') {
        // lookup handle
        if (!handle) {
            handle = this.getHandleFromUrl();

            // NEW: no handle & preview page with ProductJson already parsed -> return first found json
            if(!handle && this.isProductPreview()){
                for(const handleKey in ShopifyStore.products){
                    if(ShopifyStore.products.hasOwnProperty(handleKey)){
                        return new Promise((resolve, reject) => {
                            resolve(ShopifyStore.products[handleKey]);
                        });
                    }
                }
            }
        }

        let handleKey = handle + '.' + view;

        // check if already loaded
        if (ShopifyStore.products[handleKey]) {
            return new Promise((resolve, reject) => {
                resolve(ShopifyStore.products[handleKey]);
            });
        }

        // HACKY: editorMode (app backend) product lookup (storefront can be password protected)
        if(!window.Shopify && window._getProduct){
            return window._getProduct(handle).then((product) => {
                // store in cache
                handleKey = product.handle + '.' + view;
                ShopifyStore.products[handleKey] = product;
            }).catch((error) => {
                throw error;
            });
        }

        let productUrl;

        // NEW: support product preview WITHOUT ProductJson in html
        if(handle) {
            productUrl = this.settings.api + this.settings.path.products.replace('{handle}', handle).replace('{view}', view);
        } else if(this.isProductPreview()){
            // preview current url with view
            productUrl = window.location.href.replace('products_preview', 'products_preview.' + view);
        }

        // call ajax api (  maybe we need to use cache busting with random url param ?_=1566499721696  - to view merchant product changes faster  )
        let request = new Request(productUrl, {
            method: 'GET',
            responseType: 'json'
        });

        return request
            .then((response) => {
                // store in cache
                let product = response.data;
                // json is one nested
                if(view === 'json'){
                    product = response.data.product;
                }
                // NEW: rebuild handle key (edge case where a product has multiple URLs for the same handle)
                handleKey = product.handle + '.' + view;
                // store in cache
                ShopifyStore.products[handleKey] = product;
                // return product json
                return product;
            })
            .catch((error) => {
                throw error;
            });
    }

    isTheme(theme){
        if(window.Shopify && window.Shopify.theme){
            const themeStoreId = window.Shopify.theme.theme_store_id;
            // fixed theme ids (to allow theme renaming)  NEW: dawn based themes
            if(theme === 'Dawn' && (themeStoreId === 887 || themeStoreId === 1368 || themeStoreId === 1363 || themeStoreId === 1356 || themeStoreId === 1431 || themeStoreId === 1434 || themeStoreId === 1499 || themeStoreId === 1500 || themeStoreId === 1567 || themeStoreId === 1864 || themeStoreId === 1841 || themeStoreId === 1891 || themeStoreId === 2699) ){
                return true;
            }
            // NEW: Horizon 1.0+  (detect by unique custom elements to avoid hardcoding theme ids)
            if(theme === 'Horizon' && customElements.get('fly-to-cart') && customElements.get('dialog-component')){
                return true;
            }
            if(theme === 'Expanse' && (themeStoreId === 902) ){
                return true;
            }
            // default substring in theme name
            if(window.Shopify.theme.name && window.Shopify.theme.name.toLowerCase().indexOf(theme.toLowerCase()) !== -1){
                return true;
            }
        }
        return false;
    }

    isThemeEditor(){
        // https://shopify.dev/themes/architecture/sections/integrate-sections-with-the-theme-editor#javascript
        return window.Shopify && window.Shopify.designMode;
    }

    onThemeSectionEvent(event, listener){
        // https://shopify.dev/themes/architecture/sections/integrate-sections-with-the-theme-editor
        return document.addEventListener('shopify:section:' + event, listener);
    }

    findProductForms(el = document){
        return el.querySelectorAll('form[action*="/cart/add"]');
    }

    findProductFormBestMatch(productId, searchOptions){

        // find variant id for this product form
        // specific form selector ( required for multiple products on single page support )
        let formSelector = '#product_form_' + productId; // this id is generated by {% form 'product', product %} ( not available in all themes ! )

        let productForm = document.querySelector(formSelector);

        // NEW: shopify shop pay app generates a product form with the main product form id (very stupid)
        if(productForm && productForm.querySelector('shopify-payment-terms') != null){
            productForm = null; // reset to not use this form
        }

        // NEW: check if there is a custom data-product-form attribute REQUIRED to avoid the default from id take precedence
        if(productForm && document.querySelector(`[data-product-form="${productId}"]`)){
            productForm = null; // reset to not use this form
        }

        // NEW: auto reset installment forms
        if(productForm && this.isProductFormInstallment(productForm)){
            productForm = null; // reset to not use this form
        }

        // NEW: dawn specific add to cart form
        if(productForm && this.isTheme('Dawn')){
            let dawnAddToCartForm = document.querySelector('[data-type="add-to-cart-form"]');
            if(dawnAddToCartForm){
                // auto mark with data-product-form (to avoid the need to manually mark the main form with data-product-form)
                dawnAddToCartForm.dataset.productForm = productId.toString();
                // IMPORTANT: we reset the form to find the PARENT element (required to find the variants)
                productForm = null;
            }
        }

        // NEW: app block always reset the product form  (new themes have so many forms with wrong #product_form_id)
        //      class="shopify-block shopify-app-block" added by shopify theme {% render block %}
        if(productForm && searchOptions.srcEl && searchOptions.srcEl.closest('.shopify-app-block')){
            productForm = null; // reset to search via parent (finds the closest form)
        }

        // general product form selector if not found
        if(!productForm){
            let productForms = null;

            // NEW: traverse upwards until we find a product form
            if(searchOptions && searchOptions.srcEl){
                let parentNode = searchOptions.srcEl.parentNode;
                while(parentNode){
                    // find all product forms from this parentNode
                    productForms = this.findProductForms(parentNode);
                    if(productForms.length){
                        // save parent node reference
                        searchOptions.parentEl = parentNode;
                        break;
                    }
                    parentNode = parentNode.parentNode;
                }
            }

            // fallback find all productForms
            if(productForms == null || productForms.length === 0){
                productForms = this.findProductForms();
            }

            // single match
            if(productForms.length === 1){
                return productForms[0];
            }
            // multiple match
            else if(productForms.length > 1){

                let productIdForm = null;
                for (let i = 0; i < productForms.length; i++) {
                    // NEW: online store 2.0  installments form (manual exclude)
                    if(this.isProductFormInstallment(productForms[i])){
                        continue;
                    }
                    // NEW: only assign after installment check
                    productForm = productForms[i];
                    // NEW: custom data-product-form attribute > return first match
                    if(productForm.dataset.productForm === productId.toString()){
                        return productForm;
                    }
                    if(!productIdForm && productForm.querySelector(`[name="product-id"][value="${productId}"]`)){
                        productIdForm = productForm;
                    }
                }
                // NEW: last form belongs to another product (e.g. upsell form in the same section) > use the product-id match
                const productIdEl = productForm && productForm.querySelector('[name="product-id"]');
                if(productIdForm && productIdEl && productIdEl.value !== productId.toString()){
                    return productIdForm;
                }
            }
        }
        return productForm;
    }

    isProductFormInstallment(productFormEl){
        let formId = productFormEl.getAttribute('id');
        if(formId){
            formId = formId.toLowerCase(); // NEW: some themes use uppercase in installment form
            // NEW: installment/instalment both names || NEW: Dawn quick-add (product recommendations)  || NEW: Impulse (product complementary)                                // NEW: className check for installment                                                                   // NEW: shop pay installments form
            if((formId.indexOf('installment') !== -1 || formId.indexOf('instalment') !== -1 || formId.indexOf('quick-add') !== -1 || formId.indexOf('complementary-') !== -1) || (productFormEl.className.indexOf('installment') !== -1 || productFormEl.className.indexOf('instalment') !== -1) || productFormEl.querySelector('shopify-payment-terms') != null){
                return true;
            }
        }

        return false;
    }

    findProductFormVariantIdEl(productFormEl){
        if(productFormEl != null){

            // NEW: always prefer direct link (only works when form object is passed)
            // link of element outside of form docs: https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement#accessing_the_forms_elements
            if(productFormEl.elements instanceof HTMLFormControlsCollection){
                let variantIdEl = productFormEl['id']; // gets the element with name="id"

                if(variantIdEl instanceof HTMLInputElement){
                    return variantIdEl;
                }
            }

            // select element with name="id" which should be the current variant id  // NEW: some themes use id[]
            let variantIdElList = productFormEl.querySelectorAll('[name=id], [name="id[]"]');

            for (let i = 0; i < variantIdElList.length; i++) {
                let variantIdEl = variantIdElList[i];

                if(variantIdEl != null && variantIdEl.value){

                    if(variantIdElList.length > 1){
                        // NEW: exclude installment forms (some themes do not update the variant id correctly)
                        let form = variantIdEl.closest('form');
                        if(form && this.isProductFormInstallment(form)){
                            continue;
                        }
                        // NEW: exclude disabled variant id fields (some themes do not update the variant id correctly)
                        if(variantIdEl.disabled){
                            continue;
                        }
                    }

                    return variantIdEl;
                }

            }

        }

        return null;
    }

    findProductFormVariantId(productFormEl, parentEl){

        // NEW: always check with the form el first - to use the ['id'] direct link
        let variantIdEl = this.findProductFormVariantIdEl(productFormEl);

        // fallback to parentEl if not found
        if(!variantIdEl && parentEl){
            variantIdEl = this.findProductFormVariantIdEl(parentEl);
        }

        if(variantIdEl){
            return variantIdEl.value;
        }

        return null;
    }

    onVariantIdChange(productFormEl, listener){
        let variantIdEl = this.findProductFormVariantIdEl(productFormEl);

        if(variantIdEl){
            // radio input group can return list of elements
            if(Util.isNodeList(variantIdEl)){
                for (let i = 0; i < variantIdEl.length; i++) {
                    const idEl = variantIdEl[i];
                    idEl.addEventListener('change', (event) => listener(event, idEl.value));
                }
            } else {
                variantIdEl.addEventListener('change', (event) => listener(event, variantIdEl.value));

                // Standard-event themes can directly set input.value without a "change" event.
                this.onProductVariantUpdate(productFormEl, (event, update) => {
                    // Keep this deferred so the global extended-variant listener
                    // can add variants beyond the AJAX product response limit first.
                    // It also lets the theme update/clear the hidden input.
                    setTimeout(() => listener(event, update.variantId || variantIdEl.value));
                });
            }

        }
    }

    /**
     * Watch the standard storefront product selection event and normalize its
     * async result. Also supports Horizon's legacy 1.x-3.x variant:update event.
     *
     * @param {EventTarget} element event scope used by the caller
     * @param {Function} listener receives (event, { variant, variantId, product, selectedOptions, html, productId, newProduct })
     * @returns {Function} remove all registered listeners
     */
    onProductVariantUpdate(element, listener){
        const normalizeVariantId = (variantId) => {
            if(variantId === null || typeof variantId === 'undefined'){
                return null;
            }
            return variantId.toString().replace('gid://shopify/ProductVariant/', '');
        };

        const notify = (event, variant, data = {}) => {
            listener(event, {
                variant: variant || null,
                variantId: normalizeVariantId(variant?.id),
                product: event.product,
                selectedOptions: event.selectedOptions,
                html: data.html,
                productId: data.productId,
                newProduct: data.newProduct
            });
        };

        // Horizon 1.x-3.x: result is available directly on event.detail.
        const legacyListener = (event) => {
            notify(event, event.detail?.resource, event.detail?.data);
        };

        // Standard storefront event: dispatched before the variant selection
        // completes, with the result delivered through the event promise.
        const standardListener = (event) => {
            if(!event.promise || typeof event.promise.then !== 'function'){
                return;
            }

            event.promise.then((result) => {
                let data = result?.detail || {};
                // detail.resource is Horizon's full AJAX variant. The standard
                // result.variant is a fallback and may contain a Shopify GID.
                let variant = data.resource || result?.variant || null;
                notify(event, variant, data);
            }).catch((error) => {
                // Rapid selections abort the previous variant request.
                if(error?.name !== 'AbortError'){
                    log.warn('Standard product variant update failed', error);
                }
            });
        };

        const legacyHorizon = this.isTheme('Horizon');
        if(legacyHorizon){
            element.addEventListener('variant:update', legacyListener);
        }
        element.addEventListener('shopify:product:select', standardListener);

        return () => {
            if(legacyHorizon){
                element.removeEventListener('variant:update', legacyListener);
            }
            element.removeEventListener('shopify:product:select', standardListener);
        };
    }

    findProductFormQuantityEl(productFormEl, fallbackEl){
        if(productFormEl != null){
            // select element with name="quantity" which should contain the selected quantity
            return productFormEl.querySelector('[name=quantity]') || (fallbackEl ? fallbackEl.querySelector('[name=quantity]') : null);
        }
        return null;
    }

    findProductFormQuantity(productFormEl, fallbackEl){
        // select element with name="quantity" which should contain the selected quantity
        let quantityEl = this.findProductFormQuantityEl(productFormEl, fallbackEl);
        if(quantityEl != null){
            return parseInt(quantityEl.value);
        }
        return 1; // default 1
    }

    getFormProperties(productFormEl){

        // use native FormData to parse form like the browser would AND also support other apps adding properties
        const data = new FormData(productFormEl);

        // map to json object source: https://dev.to/jordanfinners/how-to-easily-convert-html-form-to-json-3pea
        return Array.from(data.keys()).reduce((result, key) => {

            // only add top level properties (ignore theme inputs like items[0][properties][name])
            if (key.indexOf('properties[') === 0) {
                // unwrap property name
                let start = key.indexOf('[');
                let end = key.indexOf(']');
                if (start !== -1 && end !== -1) {
                    let property = key.substring(start + 1, end);

                    // IMPORTANT: property overwrite support (same as shopify add to cart , accept the last added property in the form )
                    if (result.hasOwnProperty(property)) {
                        let values = data.getAll(key);
                        result[property] = values[values.length - 1];
                    } else {
                        result[property] = data.get(key);
                    }

                }
            }

            return result;
        }, {});
    }

    findExpressPaymentButtons(root = document){
        return root.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]');
    }

    findExpressDynamicCheckoutCart(root = document){
        return root.querySelectorAll('#dynamic-checkout-cart, [data-shopify="dynamic-checkout-cart"]');
    }

    isExpressPaymentButton(element) {
        return Util.matchesIncludeParent(element, '.shopify-payment-button, [data-shopify="payment-button"]')
    }

    onProductFormSubmit(form, parent, listener, capture, wrapperSelectors = ['[data-live-validate]']){

        let addToCartSelectors = '[type=submit],.add_to_cart,.add-to-cart,.product-addToCart,.addToCart,.addtocart-js';

        // NEW: dawn theme quick add (product recommendations) on product page can cause add to cart trigger (requires manual exclude)
        if(this.isTheme('Dawn')){
            addToCartSelectors = addToCartSelectors.replace('[type=submit]', '[type=submit]:not(.quick-add__submit)');
        }

        let wrapperBtns = parent.querySelectorAll(wrapperSelectors.join(','));

        // disable capture if a explicit wrapper button is provided
        if(wrapperBtns.length){
            capture = false;
        }

        if(capture){

            // add express checkout selectors
            const captureSelectors = addToCartSelectors + ',.shopify-payment-button, [data-shopify="payment-button"]';

            /* hardcore variant that listens for every click on the form */
            parent.addEventListener('click', (e) => {
                if(e.target){
                    // check if the target in an add to cart button OR IMPORTANT: could be a child of a button (text/svg) we have to detect this by checking the parent of the target
                    if(Util.matchesIncludeParent(e.target, captureSelectors)){
                        listener(e);
                    }
                }
            }, { capture: true });

        } else {
            // normal form submit ( is only triggered by button/input type=submit and NOT by manual form.submit()
            form.addEventListener('submit', listener);
            // ajax from submit get via click handler
            let addBtns = form.querySelectorAll(addToCartSelectors);
            for (let i = 0; i < addBtns.length; i++) {
                const addBtn = addBtns[i];
                addBtn.addEventListener('click', listener);
            }

            let found = addBtns.length;

            // shopify express payment button ( custom event listener with capture: true )
            // capture executes the event handlers from top to button instead of bubbling up form the event target
            // this allows us to execute the click handler before the shopify button handler and prevent the logic if the field is not valid
            let expressBtns = this.findExpressPaymentButtons(form);
            for (let i = 0; i < expressBtns.length; i++) {
                const expressBtn = expressBtns[i];
                expressBtn.addEventListener('click', listener, { capture: true });
            }

            // custom wrapper button to capture click on add to cart
            for (let i = 0; i < wrapperBtns.length; i++) {
                const wrapperBtn = wrapperBtns[i];
                wrapperBtn.addEventListener('click', listener, { capture: true });

                found = true;
            }

            if(!found){
                log.warn('[live-options] failed to find add to cart button');
            }

            return found;
        }
    }

    onCartFormSubmit(listener, capture, wrapperSelectors = ['[data-live-checkout]']){

        // IMPORTANT: some themes use /checkout AND multi language($) AND custom query params(?)
        let selector = 'form[action="/cart"], form[action$="/cart"], form[action*="/cart?"], form[action="/checkout"], form[action$="/checkout"], form[action*="/checkout?"]';

        // NEW: add checkout links to wrapper selector (checks every click)    NEW: capture button with onclick="window.location='/checkout'"
        wrapperSelectors.push('a[href="/checkout"], a[href$="/checkout"], a[href*="/checkout?"], button[onclick*="/checkout"]');

        // Global submit listener with capture flag - makes sure to always execute first AND catches dynamically added forms
        // INFO: has to be disabled if other apps require validation on the checkout page
        if(capture){
            document.addEventListener('submit', (e) => {
                // check if the submitted form is the cart form
                if(e.target && Util.matches(e.target, selector)){
                    listener(e);
                }
            }, { capture: true });

            // NEW: aggressive click with wrapperSelector target support (very inefficient to check on every click - but this makes sure to catch every click on the provided wrapper selector)
            const captureSelector = wrapperSelectors.join(',');
            document.addEventListener('click', (e) => {
                if(e.target){
                    // check if the target matches the wrapperSelector OR IMPORTANT: could be a child of a button (text/svg) we have to detect this by checking the parent of the target
                    if(Util.matches(e.target, captureSelector) || Util.findParent(e.target, captureSelector) != null){
                        listener(e);
                    }
                }
            }, { capture: true });

        } else {

            // FALLBACK: attach to all forms found on page
            let cartFormElList = document.querySelectorAll(selector);

            if(cartFormElList.length){
                for (let i = 0; i < cartFormElList.length; i++) {
                    const cartFormEl = cartFormElList[i];
                    cartFormEl.addEventListener('submit', (e) => {
                        listener(e);
                    });
                }
            }

            let wrapperBtns = document.querySelectorAll(wrapperSelectors.join(','));

            // custom wrapper button to capture click on checkout
            for (let i = 0; i < wrapperBtns.length; i++) {
                const wrapperBtn = wrapperBtns[i];
                wrapperBtn.addEventListener('click', listener, { capture: true });
            }
        }

    }

    // return the amount only without currency symbol
    trimMoneyFormatAmount(moneyFormat) {
        // return
        let formatStart = moneyFormat.indexOf('{')
        let formatEnd = moneyFormat.lastIndexOf('}');

        // cut currency from format
        if(formatStart !== -1 && formatEnd !== -1){
            moneyFormat = moneyFormat.substring(formatStart, formatEnd + 1);
        }

        return moneyFormat.trim();
    }

    parseImageDimension(src){

        // https://cdn.shopify.com/s/files/1/0245/2530/8009/products/main_1024x1024@2x.jpg?v=1626278604
        // https://cdn.shopify.com/s/files/1/0245/2530/8009/products/main_300x@2x.jpg?v=1626278604

        let dimension = { x: 0, y: 0 };
        let dimensionRegex = /(\d*)x(\d*)/;
        let result = dimensionRegex.exec(src);
        if(result && result.length === 3) {
            dimension.x = parseInt(result[1]) || 0; // fallback to 0
            dimension.y = result[2] ? parseInt(result[2]) : dimension.x; // handle x only
        }
        return dimension;
    }

    stripImageDimension(src){
        src = src.replace(/_(\d*)x(\d*)/g,'');
        let retina = src.lastIndexOf('@');
        if(retina !== -1){
            src = src.substring(0, retina);
        }
        let progressive = src.lastIndexOf('.progressive');
        if(progressive !== -1){
            src = src.substring(0, progressive);
        }
        return src;
    }
}

// product data cache with handle(.view) as key
ShopifyStore.products = {};
// collection data currently not supported
ShopifyStore.collections = {};

export default ShopifyStore;
